<template>
  <div class="detail-page">
    <header class="detail-header">
      <button type="button" class="nav-action" aria-label="返回开票记录" @click="back">‹</button>
      <strong>开票详情</strong>
    </header>
    <main v-if="detail" class="consumer-scroll">
      <section class="consumer-section">
        <div class="consumer-section-head detail-status">
          <span class="status-badge" :class="statusClass">{{ detail.status }}</span>
        </div>
        <div class="detail-grid">
          <div class="detail-row"><span>申请单号</span><b>{{ detail.request_no }}</b></div>
          <div class="detail-row"><span>发票类型</span><b>{{ detail.invoice_type_text }}</b></div>
          <div class="detail-row"><span>发票抬头</span><b>{{ detail.invoice_title }}</b></div>
          <div class="detail-row"><span>申请金额</span><b>¥{{ money(detail.total_amount) }}</b></div>
          <div class="detail-row"><span>收票邮箱</span><b>{{ detail.receiver_email || '-' }}</b></div>
          <div class="detail-row"><span>申请时间</span><b>{{ formatDate(detail.submitted_at) }}</b></div>
        </div>
      </section>
      <section class="consumer-section">
        <div class="consumer-section-head"><h2>订单明细</h2></div>
        <div class="order-detail">
          <div v-for="(order, index) in orders" :key="order.order_sn + '-' + index" class="order-row">
            <strong>{{ order.goods_name || '充值订单' }}</strong>
            <div><span>订单号</span><b>{{ order.order_sn }}</b></div>
            <div><span>支付金额</span><b>¥{{ money(order.order_amount) }}</b></div>
          </div>
        </div>
      </section>
      <p v-if="isSuccess" class="consumer-notice">发票开具完成后，将直接发送到您邮箱。</p>
      <section v-if="failureReason" class="consumer-section failure-reason">
        <h3>失败原因</h3>
        <p>{{ failureReason }}</p>
      </section>
      <section class="consumer-section">
        <div class="consumer-section-head"><h2>处理进度</h2></div>
        <ul class="timeline">
          <li>{{ detail.status }}<time>{{ statusTime }}</time></li>
          <li>申请已提交<time>{{ formatDate(detail.submitted_at) }}</time></li>
        </ul>
      </section>
      <button v-if="detail.can_resubmit" type="button" class="primary-button" @click="resubmit">修改后重新提交</button>
    </main>
  </div>
</template>

<script>
import { callHideToolbar, phoneBackButtonListener } from "@/bridge/AppJsBridge";
import { getInvoiceRecordDetail } from "@/apis/invoiceCenter";
import { consumeInvoiceSession } from "./session";
import { closeInvoiceCenter, formatMoney, formatTime, handleInvoiceError } from "./common";
import { ensureInvoiceTrackingContext, getInvoiceType, trackInvoiceEvent } from "./tracking";

export default {
  data() { return { session: '', detail: null }; },
  computed: {
    statusClass() {
      if (!this.detail) return 'pending';
      if (this.detail.status === '开票成功') return 'success';
      if (this.detail.status === '开票失败') return 'failed';
      return 'pending';
    },
    isSuccess() { return this.detail && this.detail.status === '开票成功'; },
    isFailed() { return this.detail && this.detail.status === '开票失败'; },
    failureReason() {
      if (!this.isFailed) return '';
      return this.detail.can_resubmit ? (this.detail.failure_reason || '') : '开票失败，请联系客服人工处理';
    },
    orders() { return (this.detail && this.detail.orders) || []; },
    statusTime() {
      if (!this.detail) return '-';
      return formatTime(this.detail.status_changed_at || this.detail.submitted_at);
    },
  },
  async created() {
    this.session = consumeInvoiceSession(this.$route);
    callHideToolbar(true);
    phoneBackButtonListener(() => this.back());
    try {
      await ensureInvoiceTrackingContext(this.session);
      await this.load();
    } catch (error) {
      handleInvoiceError(error);
    }
  },
  beforeDestroy() { callHideToolbar(false); },
  methods: {
    money: formatMoney,
    formatDate: formatTime,
    back() {
      trackInvoiceEvent("INVOICE_DETAIL_BACK_CLICK", {
        request_no: this.detail && this.detail.request_no,
        request_status: this.detail && (this.detail.status_code || this.detail.status),
        request_status_name: this.detail && this.detail.status,
      });
      this.$router.back();
    },
    closePage() {
      trackInvoiceEvent("INVOICE_CENTER_CLOSE_CLICK", {
        from_page: "invoice_detail",
        request_no: this.detail && this.detail.request_no,
      });
      closeInvoiceCenter();
    },
    resubmit() {
      trackInvoiceEvent("INVOICE_COMPANY_RESUBMIT", {
        request_no: this.detail.request_no,
        request_status: this.detail.status_code || this.detail.status,
        request_status_name: this.detail.status,
        invoice_type: getInvoiceType(this.detail.invoice_type || this.detail.invoice_type_text),
      });
      this.$router.push({ path: '/gameJump/invoiceCenter', query: { resubmit: this.detail.request_no } });
    },
    async load() {
      this.detail = await getInvoiceRecordDetail(this.session, this.$route.params.requestNo);
      trackInvoiceEvent("INVOICE_DETAIL_VIEW", {
        request_no: this.detail.request_no,
        invoice_type: getInvoiceType(this.detail.invoice_type || this.detail.invoice_type_text),
        request_status: this.detail.status_code || this.detail.status,
        request_status_name: this.detail.status,
        request_amount: Number(formatMoney(this.detail.total_amount)),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-page { min-height: 100%; padding-bottom: 24px; color: #252a31; background: #f6f7f9; }
.detail-header { display: grid; grid-template-columns: 32px 1fr 32px; align-items: center; min-height: 54px; padding: 0 16px; border-bottom: 1px solid #eceef1; background: #fff; }
.detail-header strong { font-size: 17px; font-weight: 650; text-align: center; }
.nav-action { display: inline-flex; width: 32px; height: 32px; align-items: center; justify-content: center; padding: 0; border: 0; border-radius: 4px; color: #5f6670; background: transparent; font-size: 24px; line-height: 1; }
.consumer-scroll { padding: 12px; }
.consumer-section { margin-bottom: 10px; padding: 14px; border: 1px solid #eceef1; border-radius: 6px; background: #fff; }
.consumer-section-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.consumer-section-head h2 { margin: 0; font-size: 14px; }
.detail-status { justify-content: flex-start; }
.status-badge { display: inline-flex; min-height: 22px; align-items: center; padding: 0 8px; border-radius: 3px; font-size: 11px; white-space: nowrap; }
.success { color: #16824c; background: #e8f7ef; }
.failed { color: #c53d3d; background: #ffeded; }
.pending { color: #126ad0; background: #eaf3ff; }
.detail-grid { display: grid; gap: 9px; }
.detail-row { display: flex; justify-content: space-between; gap: 20px; line-height: 1.5; }
.detail-row span { color: #858c96; }
.detail-row b { max-width: 64%; text-align: right; word-break: break-all; }
.order-detail { display: grid; gap: 8px; }
.order-row { padding: 10px; border: 1px solid #eceef1; border-radius: 5px; }
.order-row strong { display: block; margin-bottom: 6px; font-size: 12px; }
.order-row div { display: flex; justify-content: space-between; gap: 12px; margin-top: 4px; color: #858c96; font-size: 11px; }
.order-row b { max-width: 60%; color: #515963; text-align: right; word-break: break-all; }
.consumer-notice { margin: 10px 2px; color: #858c96; font-size: 11px; line-height: 1.6; }
.failure-reason { border-color: #f2c7c7; background: #fffafa; }
.failure-reason h3 { margin: 0 0 8px; color: #b83f3f; font-size: 13px; }
.failure-reason p { margin: 0; color: #7b4747; font-size: 12px; line-height: 1.65; }
.timeline { margin: 0; padding: 0; list-style: none; }
.timeline li { position: relative; padding: 0 0 16px 20px; color: #555d68; line-height: 1.45; }
.timeline li::before { position: absolute; top: 3px; left: 2px; width: 8px; height: 8px; border-radius: 50%; content: ""; background: #1677ff; }
.timeline li::after { position: absolute; top: 13px; bottom: 0; left: 5px; width: 1px; content: ""; background: #d9dee5; }
.timeline li:last-child { padding-bottom: 0; }
.timeline li:last-child::after { display: none; }
.timeline time { display: block; margin-top: 4px; color: #959ca6; font-size: 11px; }
.primary-button { width: 100%; min-height: 40px; padding: 0 18px; border: 1px solid #1677ff; border-radius: 4px; color: #fff; background: #1677ff; }
</style>
